<template>
  <div data-test="graphics-pollDisplaySettings">
    <SidebarSettingsGroup
      title="Poll Graphic Settings"
      collapsible
      :element-type="element.type"
      data-test="graphics-pollDisplaySettings-general"
    >
      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="pollSelected"
            class="sidebar-form-label"
          >Poll</label>
        </div>
        <div class="settings-col-12">
          <div class="poll-dropdown-wrapper">
            <div
              class="poll-dropdown-trigger"
              tabindex="0"
              @click="togglePollDropdown"
              @keydown.enter="togglePollDropdown"
              @keydown.space.prevent="togglePollDropdown"
            >
              <span class="poll-dropdown-value">
                {{ selectedPollTitle }}
              </span>
              <span class="poll-dropdown-arrow">▼</span>
            </div>
            <div
              v-if="showPollDropdown"
              class="poll-dropdown-menu"
            >
              <div class="poll-search-wrapper">
                <input
                  ref="pollSearchInput"
                  v-model="pollSearchQuery"
                  type="text"
                  class="poll-search-input"
                  placeholder="Search polls..."
                  @input="onPollSearchInput"
                >
              </div>
              <div
                ref="pollDropdownList"
                class="poll-dropdown-list"
                @scroll="onPollScroll"
              >
                <div
                  v-for="poll in filteredPolls"
                  :key="poll.pollId"
                  class="poll-dropdown-item"
                  :class="{ 'active': poll.pollId === selectedPollId }"
                  :data-test="`graphics-selectPoll-${poll.pollId}`"
                  @click="selectPoll(poll)"
                >
                  {{ poll.title }}
                </div>
                <div
                  v-if="loadingPolls"
                  class="poll-dropdown-loading"
                >
                  Loading...
                </div>
                <div
                  v-if="!loadingPolls && filteredPolls.length === 0"
                  class="poll-dropdown-empty"
                >
                  No polls found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="displaySelected"
            class="sidebar-form-label"
          >Layout</label>
        </div>
        <div class="settings-col-12">
          <div class="display-dropdown-wrapper">
            <div
              class="display-dropdown-trigger"
              :class="{ 'disabled': !selectedPollId }"
              :tabindex="selectedPollId ? 0 : -1"
              @click="selectedPollId && toggleDisplayDropdown()"
              @keydown.enter="selectedPollId && toggleDisplayDropdown()"
              @keydown.space.prevent="selectedPollId && toggleDisplayDropdown()"
            >
              <span class="display-dropdown-value">
                {{ selectedDisplayTitle }}
              </span>
              <span class="display-dropdown-arrow">▼</span>
            </div>
            <div
              v-if="showDisplayDropdown"
              class="display-dropdown-menu"
            >
              <div class="display-search-wrapper">
                <input
                  ref="displaySearchInput"
                  v-model="displaySearchQuery"
                  type="text"
                  class="display-search-input"
                  placeholder="Search layouts..."
                >
              </div>
              <div class="display-dropdown-list">
                <div
                  v-for="display in filteredDisplays"
                  :key="display.displayId"
                  class="display-dropdown-item"
                  :class="{ 'active': display.displayId === displayId }"
                  :data-test="`graphics-selectPollDisplay-${display.displayId}`"
                  @click="selectDisplay(display)"
                >
                  {{ display.title }}
                </div>
                <div
                  v-if="filteredDisplays.length === 0"
                  class="display-dropdown-empty"
                >
                  No layouts found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="roundSelected"
            class="sidebar-form-label"
          >Round</label>
        </div>
        <div class="settings-col-12">
          <div class="round-dropdown-wrapper">
            <div
              class="round-dropdown-trigger"
              tabindex="0"
              @click="toggleRoundDropdown"
              @keydown.enter="toggleRoundDropdown"
              @keydown.space.prevent="toggleRoundDropdown"
            >
              <span class="round-dropdown-value">
                {{ selectedRoundTitle }}
              </span>
              <span class="round-dropdown-arrow">▼</span>
            </div>
            <div
              v-if="showRoundDropdown"
              class="round-dropdown-menu"
            >
              <div class="round-search-wrapper">
                <input
                  ref="roundSearchInput"
                  v-model="roundSearchQuery"
                  type="text"
                  class="round-search-input"
                  placeholder="Search rounds..."
                >
              </div>
              <div class="round-dropdown-list">
                <div
                  v-for="round in filteredRounds"
                  :key="`round-${round.originalIndex}`"
                  class="round-dropdown-item"
                  :class="{ 'active': round.originalIndex === currentRoundIndex }"
                  @click="selectRound(round.originalIndex)"
                >
                  {{ round.title || `Round ${round.originalIndex + 1}` }}
                </div>
                <div
                  v-if="filteredRounds.length === 0"
                  class="round-dropdown-empty"
                >
                  No rounds found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label class="sidebar-form-label">Display State</label>
        </div>
        <div class="settings-col-12">
          <div class="poll-state">
            <label
              v-for="state in displayStates"
              :key="state"
              class="poll-state__label"
              :class="{
                'poll-state__label--active': currentDisplayState === state,
              }"
            >
              <input
                v-model="currentDisplayState"
                type="radio"
                :value="state"
                class="poll-state__input"
              />
              {{ stateLabels[state] || state }}
            </label>
          </div>
        </div>
      </div>

      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label class="sidebar-form-label">Question</label>
        </div>
        <div class="settings-col-12">
          <div
            class="data-list__page-controls data-list__page-controls--no-border"
          >
            <button
              class="btn btn-sm btn-icon btn-style-b"
              :disabled="currentQuestionIndex <= 1"
              data-test="graphics-pollDisplay-previousQuestion"
              @click="currentQuestionIndex--"
            >
              <span class="fas fa-backward" />
            </button>

            <input
              v-model.number="currentQuestionIndex"
              type="number"
              :min="1"
              :max="questions.length"
              class="form-control"
              data-test="graphics-pollDisplay-questionInput"
            />

            <button
              class="btn btn-sm btn-icon btn-style-b"
              data-test="graphics-pollDisplay-nextQuestion"
              :disabled="currentQuestionIndex >= questions.length"
              @click="currentQuestionIndex++"
            >
              <span class="fas fa-forward" />
            </button>
          </div>
        </div>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-pollDisplaySettings-styleAndAnimation"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">Background color</label>
        <label
          class="radio inline-radio"
          for="solid"
        >
          Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-pollDisplaySettings-fillStyleSolid"
            data-entity="graphics-pollDisplaySettings-fillStyleSolid"
          />
          <div class="checkmark" />
        </label>

        <label
          class="radio inline-radio"
          for="gradient"
        >
          Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-pollDisplaySettings-fillStyleGradient"
            data-entity="graphics-pollDisplaySettings-fillStyleGradient"
          />
          <div class="checkmark" />
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker text-center"
          data-test="graphics-pollDisplaySettings-fillStyleSolidColor"
        >
          <colorpicker v-model="backgroundColor" />
        </div>

        <div
          v-else
          class="gradient-color-picker"
        >
          <div class="colorpicker text-center">
            <colorpicker v-model="gradientColorOne" />
          </div>
          <div class="colorpicker text-center">
            <colorpicker v-model="gradientColorTwo" />
          </div>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <box-shadow-settings :element="element" />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-pollDisplaySettings-toggleBorder"
            data-entity="graphics-pollDisplaySettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <input
                v-model.number="borderWidth"
                type="number"
                name="borderWidth"
                min="0"
                data-test="graphics-pollDisplaySettings-setBorderWidth"
                data-entity="graphics-pollDisplaySettings-setBorderWidth"
              />
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker v-model="borderColor" />
              <label class="sidebar-input-sub-label">Color</label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-pollDisplaySettings-toggleBlur"
            data-entity="graphics-pollDisplaySettings-toggleBlur"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="blur">
          <div class="settings-row">
            <div class="settings-col-12">
              <input
                v-model.number="blurSize"
                type="number"
                min="0"
                data-test="graphics-pollDisplaySettings-setBlurSize"
                data-entity="graphics-pollDisplaySettings-setBlurSize"
              />
              <label class="sidebar-input-sub-label">Blur Size</label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings
        v-if="!multipleSelected"
        :element="element"
      />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import Polls from '../../../../services/polls';

import SettingsMixins from '../../../../mixins/settings-mixins';
import BorderRadius from '../../BorderRadius.vue';
import BoxShadowSettings from '../../BoxShadow.vue';
import OpacitySettings from '../../Opacity.vue';
import AnimationSettings from '../../Animation.vue';

export default {
  name: 'PollDisplaySettings',

  components: {
    BorderRadius,
    BoxShadowSettings,
    OpacitySettings,
    AnimationSettings,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },
    template: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      polls: [],
      pollsDetails: {},
      pollData: null,
      showPollDropdown: false,
      pollSearchQuery: '',
      showDisplayDropdown: false,
      displaySearchQuery: '',
      showRoundDropdown: false,
      roundSearchQuery: '',
      loadingPolls: false,
      pollsPage: 1,
      pollsLimit: 20,
      pollsTotal: 0,
      allPollsLoaded: false,
    };
  },

  computed: {
    multipleSelected() {
      return this.$store.state.templates.selectedElements.length > 1;
    },

    displayId: {
      get() {
        return this.getPropertyValue('displayId');
      },
      set(v) {
        this.updateOneElementValue('displayId', v);
      },
    },

    pollId() {
      return this.getPropertyValue('pollId');
    },

    selectedPollId: {
      get() {
        return this.getPropertyValue('pollId');
      },
      set(v) {
        this.updateOneElementValue('pollId', v);
      },
    },

    availableDisplays() {
      if (!this.pollId || !this.pollsDetails[this.pollId]) {
        return [];
      }

      const displays = this.pollsDetails[this.pollId].displays || [];
      return displays.filter((d) => d.displayType !== 'poll');
    },

    rounds() {
      return this.pollData?.rounds || [];
    },

    questions() {
      const roundIndex = this.currentRoundIndex;
      return this.rounds[roundIndex]?.questions || [];
    },

    displayStates() {
      return ['preVote', 'results', 'closed'];
    },

    stateLabels() {
      return {
        preVote: 'Open',
        results: 'Results',
        closed: 'Final',
      };
    },

    filteredPolls() {
      // When searching, show the polls from API response
      // No need for client-side filtering since API handles search
      return this.polls;
    },

    filteredDisplays() {
      if (!this.displaySearchQuery) {
        return this.availableDisplays;
      }
      const query = this.displaySearchQuery.toLowerCase();
      return this.availableDisplays.filter(
        (display) => display.title.toLowerCase().includes(query),
      );
    },

    filteredRounds() {
      if (!this.roundSearchQuery) {
        return this.rounds.map((round, index) => ({ ...round, originalIndex: index }));
      }
      const query = this.roundSearchQuery.toLowerCase();
      return this.rounds
        .map((round, index) => ({ ...round, originalIndex: index }))
        .filter((round) => {
          const title = round.title || `Round ${round.originalIndex + 1}`;
          return title.toLowerCase().includes(query);
        });
    },

    selectedPollTitle() {
      const poll = this.polls.find((p) => p.pollId === this.selectedPollId);
      return poll ? poll.title : 'Select a poll';
    },

    selectedDisplayTitle() {
      const display = this.availableDisplays.find(
        (d) => d.displayId === this.displayId,
      );
      return display ? display.title : 'Select a layout';
    },

    selectedRoundTitle() {
      const roundIndex = this.currentRoundIndex;
      const round = this.rounds[roundIndex];
      return round
        ? round.title || `Round ${roundIndex + 1}`
        : 'Select a round';
    },

    currentRoundIndex: {
      get() {
        return this.getPropertyValue('currentRoundIndex', { emptyValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('currentRoundIndex', v);
        // Reset to first question when round changes
        this.currentQuestionIndex = 1;
      },
    },

    currentDisplayState: {
      get() {
        return this.getPropertyValue('currentDisplayState', {
          emptyValue: 'results',
        });
      },
      set(v) {
        this.updateOneElementValue('currentDisplayState', v);
      },
    },

    currentQuestionIndex: {
      get() {
        const idx = this.getPropertyValue('currentQuestionIndex', {
          emptyValue: 0,
        });
        return idx + 1;
      },
      set(v) {
        const idx = v - 1;
        this.updateOneElementValue('currentQuestionIndex', idx);
      },
    },

    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },

    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },

    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },

    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },

    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle', { emptyValue: 'solid' });
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },

    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', {
          emptyValue: 'transparent',
        });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },

    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', {
          emptyValue: '#00c4dd',
        });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },

    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', {
          emptyValue: '#0f84fb',
        });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth', { emptyValue: 2 });
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { emptyValue: '#000000' });
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },

    blurSize: {
      get() {
        return this.getPropertyValue('blurSize', { emptyValue: 10 });
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },

  watch: {
    pollId: {
      handler(newVal) {
        if (newVal && this.displayId) {
          this.loadPollData();
        }
      },
    },
  },

  async mounted() {
    await this.loadPolls();

    if (this.pollId) {
      await this.loadPollDisplays();

      if (this.displayId) {
        await this.loadPollData();
      }
    }

    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    async loadPolls(search = '', page = 1) {
      try {
        this.loadingPolls = true;
        const query = {
          page,
          limit: this.pollsLimit,
        };

        if (search) {
          query.search = search;
        }

        const response = await Polls.getPolls(query);

        if (!response || !response.polls) {
          if (page === 1) {
            this.polls = [];
          }
          return;
        }

        if (page === 1) {
          this.polls = response.polls;
        } else {
          this.polls = [...this.polls, ...response.polls];
        }

        this.pollsTotal = response.total || response.polls.length;
        this.pollsPage = page;
        this.allPollsLoaded = this.polls.length >= this.pollsTotal;
      } catch (error) {
        console.error('Error loading polls:', error);
        if (page === 1) {
          this.polls = [];
        }
      } finally {
        this.loadingPolls = false;
      }
    },

    debouncedLoadPolls: debounce(function (search) {
      this.loadPolls(search, 1);
    }, 300),

    onPollSearchInput() {
      this.pollsPage = 1;
      this.allPollsLoaded = false;
      this.debouncedLoadPolls(this.pollSearchQuery);
    },

    onPollScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (scrolledToBottom && !this.loadingPolls && !this.allPollsLoaded) {
        this.loadPolls(this.pollSearchQuery, this.pollsPage + 1);
      }
    },

    async loadPollDisplays() {
      if (!this.pollId) {
        return;
      }

      if (this.pollsDetails[this.pollId]) {
        return;
      }

      try {
        const pollDetails = await Polls.getPollById(this.pollId);
        this.pollsDetails[this.pollId] = pollDetails;
      } catch (error) {
        console.error('Error loading poll displays:', error);
      }
    },

    async loadPollData() {
      try {
        this.pollData = await Polls.getPollById(this.pollId);
      } catch (error) {
        console.error('Error loading poll data:', error);
      }
    },

    async onPollChange() {
      if (!this.pollId) {
        return;
      }

      // Clear display selection first to force a refresh
      this.displayId = null;

      await this.loadPollDisplays();
      await this.loadPollData();

      // Auto-select first display and reset dependent values after poll data is loaded
      if (this.availableDisplays.length > 0) {
        // Use $nextTick to ensure the displayId clear is processed
        await this.$nextTick();
        this.displayId = this.availableDisplays[0].displayId;
        await this.onDisplayChange();
      }
    },

    async onDisplayChange() {
      const display = this.availableDisplays.find(
        (d) => d.displayId === this.displayId,
      );

      if (display) {
        this.updateOneElementValue('displayId', display.displayId);
        this.updateOneElementValue('name', display.title);

        await this.loadPollData();

        // Reset to first round and first question
        this.currentRoundIndex = 0;
        this.currentQuestionIndex = 1;
      }
    },

    togglePollDropdown() {
      this.showPollDropdown = !this.showPollDropdown;
      if (this.showPollDropdown) {
        this.$nextTick(() => {
          if (this.$refs.pollSearchInput) {
            this.$refs.pollSearchInput.focus();
          }
        });
      }
    },

    toggleDisplayDropdown() {
      this.showDisplayDropdown = !this.showDisplayDropdown;
      if (this.showDisplayDropdown) {
        this.$nextTick(() => {
          if (this.$refs.displaySearchInput) {
            this.$refs.displaySearchInput.focus();
          }
        });
      }
    },

    toggleRoundDropdown() {
      this.showRoundDropdown = !this.showRoundDropdown;
      if (this.showRoundDropdown) {
        this.$nextTick(() => {
          if (this.$refs.roundSearchInput) {
            this.$refs.roundSearchInput.focus();
          }
        });
      }
    },

    selectPoll(poll) {
      this.selectedPollId = poll.pollId;
      this.showPollDropdown = false;
      this.pollSearchQuery = '';
      this.onPollChange();
    },

    selectDisplay(display) {
      this.displayId = display.displayId;
      this.showDisplayDropdown = false;
      this.displaySearchQuery = '';
      this.onDisplayChange();
    },

    selectRound(index) {
      this.currentRoundIndex = index;
      this.showRoundDropdown = false;
      this.roundSearchQuery = '';
    },

    handleClickOutside(event) {
      const pollDropdown = this.$el.querySelector('.poll-dropdown-wrapper');
      if (pollDropdown && !pollDropdown.contains(event.target)) {
        this.showPollDropdown = false;
      }

      const displayDropdown = this.$el.querySelector('.display-dropdown-wrapper');
      if (displayDropdown && !displayDropdown.contains(event.target)) {
        this.showDisplayDropdown = false;
      }

      const roundDropdown = this.$el.querySelector('.round-dropdown-wrapper');
      if (roundDropdown && !roundDropdown.contains(event.target)) {
        this.showRoundDropdown = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gradient-color-picker {
  display: flex;
  gap: 0.5rem;
}

.poll-dropdown-wrapper,
.display-dropdown-wrapper,
.round-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.poll-dropdown-trigger,
.display-dropdown-trigger,
.round-dropdown-trigger {
  width: 100%;
  display: block;
  padding: 3px;
  padding-right: 24px;
  color: #333;
  line-height: 1.35;
  border-radius: 4px;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  box-shadow: none;
  background-image: url(/img/triangle-down.svg);
  background-size: 8px auto;
  background-position: calc(100% - 8px) 50%;
  background-repeat: no-repeat;
  cursor: pointer;

  &:hover {
    border-color: #999;
  }

  &:focus,
  &:focus-within {
    border-color: #00B4CC;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(0, 180, 204, .6);
    outline: 0;
  }

  &.disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.poll-dropdown-value,
.display-dropdown-value,
.round-dropdown-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.poll-dropdown-arrow,
.display-dropdown-arrow,
.round-dropdown-arrow {
  display: none;
}

.poll-dropdown-menu,
.display-dropdown-menu,
.round-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.poll-search-wrapper,
.display-search-wrapper,
.round-search-wrapper {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.poll-search-input,
.display-search-input,
.round-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #0f84fb;
  }
}

.poll-dropdown-list,
.display-dropdown-list,
.round-dropdown-list {
  overflow-y: auto;
  max-height: 260px;
}

.poll-dropdown-item,
.display-dropdown-item,
.round-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &.active {
    background-color: #e6f2ff;
    color: #0f84fb;
  }
}

.poll-dropdown-empty,
.display-dropdown-empty,
.round-dropdown-empty,
.poll-dropdown-loading {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.poll-state {
  display: flex;
  gap: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  overflow: hidden;
  background-color: #fff;

  &__label {
    padding: 0.5rem 1rem;
    margin-bottom: 0;
    cursor: pointer;
    border: none;
    border-right: 1px solid #cbd5e1;
    background-color: #fff;
    flex: 1;
    text-align: center;
    transition: all 0.2s ease;
    color: #64748b;
    font-weight: 400;

    &:last-child {
      border-right: none;
    }

    &:hover {
      background-color: rgba(0, 180, 204, 0.1);
    }

    &--active {
      background-color: rgba(0, 180, 204, 0.15);
      color: #00b4cc;
      font-weight: 500;
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
}

.data-list__page-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;

  &--no-border {
    border: none;
  }
}
</style>
